import { useCallback, useEffect, useMemo, useState } from "react";
import { InputProps, SwitchProps } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useUserGrantsContext } from "features/user/hooks/useUserGrantsContext";
import { checkOperationPermission } from "features/auth/utils/checkOperationPermission";
import { Checkbox, CheckboxProps } from "features/common/components/Checkbox";
import { useTable } from "features/common/components/Table/useTable";
import { columns } from "./columns";
import {
  TableColumn,
  TableRow,
} from "features/common/components/Table/Table.types";
import authorizedCall from "features/common/utils/authorizedCall";
import {
  ClientControllerService,
  PagedModelEntityModelClientDto,
} from "openapi";

export const useClientsTable = () => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [relevantList, setRelevantList] = useState<number[]>([]);
  const [taskNr, setTaskNr] = useState("");
  const [searchClients, setSearchClients] = useState("");
  const [displayMessage, setDisplayMessage] = useState<string>("");

  const { userGrants } = useUserGrantsContext();
  const navigate = useNavigate();

  const handleRelevantChange: (el: number) => CheckboxProps["onChange"] =
    useCallback(
      (el: number) => (_event, checked) => {
        if (checked) {
          setRelevantList([...relevantList, el]);
        } else {
          const filtered = relevantList.filter((element) => element !== el);
          setRelevantList(filtered);
        }
      },
      [relevantList]
    );

  const exportIsPermitted = useMemo(() => {
    let isPermitted = false;

    if (userGrants) {
      isPermitted = checkOperationPermission({
        operation: "E",
        component: "Client data export",
        func: "EXPORT",
        userGrants,
      });
    }

    return isPermitted;
  }, [userGrants]);

  const relevantColumn: TableColumn = useMemo(
    () => ({
      id: "relevant",
      label: "Relevant",
      disableSort: true,
      Cell: (row: TableRow) => {
        let isChecked = false;

        if (relevantList.length > 0) {
          isChecked = relevantList.some((el) => el === row.id);
        }

        return (
          <Checkbox
            sx={{ ml: "10px" }}
            onChange={handleRelevantChange(row.id)}
            checked={isChecked}
            disabled={!exportIsPermitted}
          />
        );
      },
    }),
    [exportIsPermitted, handleRelevantChange, relevantList]
  );

  const extendedColumns = [...columns, relevantColumn];

  const table = useTable({ columns: extendedColumns });

  const {
    setIsFetchingRows,
    setRows,
    rowsPerPage,
    setRowsError,
    page,
    orderByColumnId,
    sortOrder,
    setTotalRows,
    setPage,
  } = table;

  const reqParams: Parameters<typeof ClientControllerService.getAllClients>[0] =
    useMemo(
      () => ({
        size: rowsPerPage,
        page: page,
        ...(isToggleOn ? { idList: relevantList } : {}),
        ...(searchClients ? { search: searchClients } : {}),
        ...(orderByColumnId
          ? { sort: [orderByColumnId + "," + sortOrder] }
          : {}),
      }),
      /**
       * relevantList (not in dependency array list):
       * we not need to redefine the reqParams obj for each relevantList change,
       * but we do that only when isToggledOn === true
       */
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isToggleOn, orderByColumnId, page, rowsPerPage, searchClients, sortOrder]
    );

  const getAllClients = useCallback(() => {
    setIsFetchingRows(true);

    authorizedCall(ClientControllerService.getAllClients, reqParams, true)
      .then((data: PagedModelEntityModelClientDto) => {
        setIsFetchingRows(false);
        setRows(data._embedded?.entities ?? []);
        setTotalRows(data.page?.totalElements ?? 0);
      })
      .catch((error) => {
        setIsFetchingRows(false);
        setRowsError(error?.body?.message || error?.message);
      });
  }, [reqParams, setIsFetchingRows, setRows, setRowsError, setTotalRows]);

  useEffect(() => {
    getAllClients();
  }, [getAllClients]);

  const handleOpenModal = () => setIsOpenModal(true);
  const handleCloseModal = () => setIsOpenModal(false);

  const handleTaskNr: InputProps["onChange"] = (event) =>
    setTaskNr(event.target.value);

  const handleSearchClientsChange = useCallback(
    (searched: string) => {
      setSearchClients(searched);
      setPage(0);
    },
    [setPage]
  );

  const handleReset = () => {
    setSearchClients("");
    setIsToggleOn(false);
    setDisplayMessage("");
    setTaskNr("");
    setRelevantList([]);
  };

  const handleToggle: SwitchProps["onChange"] = (_event, checked) => {
    setSearchClients("");
    setDisplayMessage("");
    setIsToggleOn(checked);
    setPage(0);
  };

  const isSwitchDisabled = relevantList.length === 0 && isToggleOn === false;

  const handleBack = () => navigate("/");

  return {
    isOpenModal,
    handleCloseModal,
    handleOpenModal,
    isToggleOn,
    handleToggle,
    relevantList,
    handleRelevantChange,
    taskNr,
    handleTaskNr,
    searchClients,
    handleSearchClientsChange,
    exportIsPermitted,
    handleReset,
    handleBack,
    table,
    isSwitchDisabled,
    displayMessage,
    setDisplayMessage,
  };
};
